import { useRef } from 'react'
import {
  Bold,
  Italic,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  List,
  ListOrdered,
  Quote,
  Link as LinkIcon,
  Image as ImageIcon,
  Loader2,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { Editor } from '@tiptap/react'
import { Button } from '@/components/ui/button'
import { useEditorImageUpload } from './useEditorImageUpload'

interface EditorToolbarProps {
  editor: Editor
}

const TOGGLES: {
  label: string
  icon: LucideIcon
  run: (e: Editor) => void
  isActive: (e: Editor) => boolean
}[] = [
  {
    label: 'Bold',
    icon: Bold,
    run: (e) => e.chain().focus().toggleBold().run(),
    isActive: (e) => e.isActive('bold'),
  },
  {
    label: 'Italic',
    icon: Italic,
    run: (e) => e.chain().focus().toggleItalic().run(),
    isActive: (e) => e.isActive('italic'),
  },
  {
    label: 'Heading 1',
    icon: Heading1,
    run: (e) => e.chain().focus().toggleHeading({ level: 1 }).run(),
    isActive: (e) => e.isActive('heading', { level: 1 }),
  },
  {
    label: 'Heading 2',
    icon: Heading2,
    run: (e) => e.chain().focus().toggleHeading({ level: 2 }).run(),
    isActive: (e) => e.isActive('heading', { level: 2 }),
  },
  {
    label: 'Heading 3',
    icon: Heading3,
    run: (e) => e.chain().focus().toggleHeading({ level: 3 }).run(),
    isActive: (e) => e.isActive('heading', { level: 3 }),
  },
  {
    label: 'Heading 4',
    icon: Heading4,
    run: (e) => e.chain().focus().toggleHeading({ level: 4 }).run(),
    isActive: (e) => e.isActive('heading', { level: 4 }),
  },
  {
    label: 'Bullet list',
    icon: List,
    run: (e) => e.chain().focus().toggleBulletList().run(),
    isActive: (e) => e.isActive('bulletList'),
  },
  {
    label: 'Numbered list',
    icon: ListOrdered,
    run: (e) => e.chain().focus().toggleOrderedList().run(),
    isActive: (e) => e.isActive('orderedList'),
  },
  {
    label: 'Quote',
    icon: Quote,
    run: (e) => e.chain().focus().toggleBlockquote().run(),
    isActive: (e) => e.isActive('blockquote'),
  },
]

export function EditorToolbar({ editor }: EditorToolbarProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const { uploadAndInsert, isUploading } = useEditorImageUpload()

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href ?? 'https://'
    const url = window.prompt('URL', previousUrl)
    if (url === null) return
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0]
    if (file) {
      await uploadAndInsert(editor, file)
    }
    event.target.value = ''
  }

  return (
    <div className="mb-1 flex flex-wrap gap-1 rounded-md border bg-muted p-1">
      {TOGGLES.map((t) => {
        const Icon = t.icon
        return (
          <Button
            key={t.label}
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => t.run(editor)}
            className={t.isActive(editor) ? 'bg-accent' : ''}
            aria-label={t.label}
          >
            <Icon />
          </Button>
        )
      })}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={setLink}
        className={editor.isActive('link') ? 'bg-accent' : ''}
        aria-label="Link"
      >
        <LinkIcon />
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={() => fileInputRef.current?.click()}
        disabled={isUploading}
        aria-label="Image"
      >
        {isUploading ? <Loader2 className="animate-spin" /> : <ImageIcon />}
      </Button>
    </div>
  )
}
